import { isGeneralOrTraumaHospital, parseEpJourneys } from './epAdvanceV2.js';

export type EpField = { key: string; label: string; required: boolean; type: 'text' | 'textarea' | 'date' | 'time' | 'number' | 'select' | 'boolean'; options?: string[] };
export type EpSection = { id: string; title: string; fields: EpField[] };
const legacyDeskMinimum = new Set(['principalName', 'eventName', 'eventDate', 'country', 'venueName', 'addressLine1', 'city']);

function section(id: string, title: string, definition: string): EpSection {
    return {
        id, title,
        fields: definition.trim().split('\n').map(line => {
            const [key, label, kind = 'text', choices = ''] = line.split('|');
            const required = label.endsWith('*') && legacyDeskMinimum.has(key);
            return { key, label: label.replace(/\*$/, ''), required,
                type: kind as EpField['type'], ...(choices ? { options: choices.split(';') } : {}) };
        }),
    };
}

const routeFields = (code: string, name: string) => section(`s4_${code}`, `${name} Route`, `
${code}Origin|Start point*|text
${code}Destination|Destination*|text
${code}Distance|Total distance*|text
${code}NormalTime|Normal travel time*|text
${code}HeavyTime|Heavy traffic time*|text
${code}Waypoints|Key waypoints*|textarea
${code}Chokepoints|Chokepoints and hazards*|textarea
${code}ProtestPoints|Protest or crowd points*|textarea
${code}OverheadThreats|Bridges or overhead threats*|textarea
${code}TransportAlternative|Alternative transport mode*|text
${code}LawEnforcementEscort|Law enforcement escort coordinated*|select|Yes;No
${code}PreDriven|Advance team pre-driven route*|select|Yes;No`);

export const EP_SURVEY_SECTIONS: EpSection[] = [
    section('s1', 'Principal and Event', `
principalName|Principal full name*|text
principalTitle|Title or position*|text
principalOrganization|Organization or company*|text
threatClass|Threat classification*|select|Unknown;Low;Medium;High
nationality|Nationality*|text
passportNumber|Passport number|text
dateOfBirth|Date of birth*|date
aliases|Aliases and public profiles|textarea
eventName|Event name or description*|text
eventType|Event type*|select|Conference;Meeting;Public speech;Private event;Site visit;Other
eventClassification|Event classification*|select|Public;Semi-public;Private;Classified
eventDate|Event date*|date
eventStartTime|Event start time*|time
eventEndTime|Event end time*|time
attendance|Anticipated attendance*|number
pressAccess|Media or press access*|select|Yes;No;Credentialed only
medicalNotes|Principal medical notes|textarea`),
    section('s2', 'Location and Address', `
country|Country*|text
venueName|Venue or building name*|text
addressLine1|Street address*|text
addressLine2|Address line 2|text
city|City*|text
state|State or province*|text
postalCode|Postal code*|text
venueType|Venue type*|select|Hotel;Conference centre;Government;Private;Outdoor;Transport hub;Other
venueOwnership|Venue ownership*|select|Public;Private;Government;Military;Restricted
primaryAccessRoad|Primary access road*|text
secureEntry|Nearest secure entry*|text
perimeterDistance|Distance to secure perimeter*|text
latitude|Latitude*|number
longitude|Longitude*|number
addressVerification|Address verification result*|select|Google geocoded;Manual exception
addressVerificationReason|Manual address exception reason|textarea
criticalJourneys|Additional critical journeys, separate origin and destination with a vertical bar|textarea`),
    section('s3', 'Venue Mapping and Layout', `
floorLevels|Floors and levels*|text
venueArea|Venue area|text
primaryEntrance|Primary entrance*|text
secondaryEntrances|Secondary entrances|textarea
emergencyExits|Emergency exits*|textarea
restrictedAreas|Restricted areas|textarea
controlRoom|Venue control room*|text
cctvCoverage|CCTV coverage*|select|Full;Partial;None
mapAnnotationsComplete|Map annotations complete*|select|Yes;No`),
    routeFields('primary', 'Primary'),
    routeFields('alternate', 'Alternate'),
    routeFields('emergency', 'Emergency'),
    routeFields('contingency', 'Contingency'),
    section('s5', 'Threat Assessment', `
venueThreatHistory|Venue threat history*|textarea
countryThreatLevel|Country threat level*|text
cityThreatLevel|City or area threat level*|text
venueVulnerabilities|Known venue vulnerabilities|textarea
protestUnrest|Protest or civil unrest intelligence*|textarea
surveillanceIndicators|Surveillance indicators|textarea
technicalSweep|Technical surveillance sweep status*|text
bombThreatHistory|Bomb threat history*|textarea
knownThreatActors|Known threat actors*|textarea
stalkerLog|Stalker or harassment log*|textarea
formerEmployees|Disgruntled former employees|textarea
ideologicalThreats|Extremist or ideological threats*|textarea
onlineThreatMonitoring|Online threat monitoring*|textarea
insiderThreat|Insider threat assessment|textarea
foreignStateInterest|Foreign state interest*|textarea
threatDescriptions|Threat actor descriptions|textarea
eventPublicProfile|Event public profile*|select|Low;Medium;High
vipAttendance|VIP or government attendance*|textarea
eventProtestIntel|Event protest intelligence*|textarea
mediaCredentialing|Media credentialing*|textarea
crowdBehaviour|Crowd behaviour assessment*|textarea
weatherThreat|Weather threat|textarea
eventIncidentHistory|Prior event incidents*|textarea
intelligenceReference|Pre-event intelligence report reference|text`),
    section('s6', 'Safe Room', `
safeRoomNumber|Room designation*|text
safeRoomFloor|Floor level*|text
safeRoomDistance|Distance from principal*|text
safeRoomRoute|Access route*|textarea
safeRoomDoor|Door type*|text
safeRoomLock|Door lock type*|text
safeRoomAlternateEgress|Alternative egress|textarea
safeRoomDimensions|Room dimensions*|text
safeRoomHardline|Telephone or hardline*|text
safeRoomRadio|Radio signal strength*|select|Excellent;Good;Weak;None
safeRoomBackupComms|Backup communications*|text
safeRoomPower|Independent power supply*|text
safeRoomAccessChecked|Access within sixty seconds confirmed*|boolean
safeRoomDoorChecked|Forced-entry resistance verified*|boolean
safeRoomCommsChecked|Independent communications confirmed*|boolean
safeRoomMedicalChecked|Medical kit verified*|boolean
safeRoomWaterChecked|Water supply verified*|boolean
safeRoomPowerChecked|Backup power verified*|boolean
safeRoomBriefedChecked|Protection team briefed*|boolean
safeRoomCoordinatesChecked|Room coordinates shared*|boolean`),
    section('s7', 'Holding Room', `
holdingLocation|Holding room location*|text
holdingProximity|Proximity to event area*|text
holdingPrivacy|Room privacy*|select|Complete;Partial;None
holdingAccess|Access control*|text
holdingLandline|Landline available*|text
holdingCatering|Catering clearance*|text
holdingAdjacent|Adjacent room use*|text
holdingExit|Secondary exit*|text`),
    section('s8', 'Venue Security Liaison', `
securityDirector|Security director name*|text
securityMobile|Security director mobile*|text
securityRadio|Security radio channel*|text
securityBriefing|Security briefing scheduled*|text
securityBriefed|Briefing status*|select|Pending;Completed;Not completed
lawAgency|Law enforcement agency*|text
lawOfficer|Law enforcement liaison*|text
lawMobile|Law enforcement mobile*|text
lawEscort|Law enforcement escort*|text
eodK9|EOD or K9 availability|text
medicalOfficer|On-site medical officer|text
traumaCentre|Nearest trauma centre*|text
ambulanceOnSite|Ambulance on site*|text
liaisonAed|AED location*|text
liaisonCasevac|Casevac route confirmed*|textarea`),
    section('s9', 'Perimeter and Access', `
outerPerimeter|Outer perimeter*|textarea
innerPerimeter|Inner perimeter*|textarea
securePerimeter|Secure perimeter*|textarea
magnetometers|Magnetometer positions*|textarea
bagSearch|Bag search protocol*|text
staffCredentials|Staff credentials*|text
uninvitedPersons|Uninvited persons protocol*|textarea
vehicleControl|Vehicle access control*|textarea
catPresence|Counter-assault team|text
k9Sweep|Pre-event K9 sweep*|text`),
    section('s10', 'Communications', `
radioSystem|Primary radio system*|text
operationsChannel|Operations channel*|text
liaisonChannel|Liaison channel*|text
emergencyChannel|Emergency channel*|text
deadZones|Dead zones identified*|textarea
deadZoneWorkaround|Dead zone workaround*|textarea
encryptedMobile|Encrypted mobile network*|text
satellitePhone|Satellite phone*|text
commsCheck|Communications check scheduled*|text
codeWords|Code words assigned*|textarea`),
    section('s11', 'Medical and Emergency', `
medicalOfficerName|On-site medical officer|text
medicalOfficerMobile|Medical officer mobile|text
nearestTrauma|Nearest trauma centre*|text
traumaDistance|Trauma centre distance and time*|text
nearestHospital|Nearest hospital*|text
ambulancePosition|Ambulance position*|text
casevacPrimary|Primary casevac route*|textarea
casevacAlternate|Alternate casevac route*|textarea
allAeds|AED locations*|textarea
principalBloodType|Principal blood type|text`),
];

export const EP_SURVEY_STEPS = [
    EP_SURVEY_SECTIONS[0], EP_SURVEY_SECTIONS[1], EP_SURVEY_SECTIONS[2],
    { id: 's4', title: 'PACE Routes', fields: EP_SURVEY_SECTIONS.slice(3, 7).flatMap(item => item.fields) },
    ...EP_SURVEY_SECTIONS.slice(7),
];

export const EP_S1_SENSITIVE_KEYS = new Set(['passportNumber', 'dateOfBirth', 'medicalNotes']);

export const EP_PACKAGE_DOCUMENTS = [
    { key: 'advanceReport', title: 'Advance Report' },
    { key: 'safeRoomBrief', title: 'Safe Room Brief' },
    { key: 'liaisonChecklist', title: 'Liaison Checklist' },
    { key: 'routeCards', title: 'PACE Route Cards' },
    { key: 'medicalPlan', title: 'Medical Plan' },
] as const;

export const EP_QA_ITEMS = [
    'Threat profiles, source links, and dates reviewed',
    'Unverified claims and open client checks labeled',
    'PACE roles and route gaps reviewed',
    'General hospitals and trauma centers reviewed',
    'All five documents reviewed and corrected',
];

export function missingSurveyFields(values: Record<string, unknown>, section: EpSection): string[] {
    const missing = section.fields.filter(field => field.required &&
        (field.type === 'boolean' ? values[field.key] !== true : String(values[field.key] ?? '').trim() === ''))
        .map(field => field.label);
    if (section.id === 's2' && values.criticalJourneys && !parseEpJourneys(String(values.criticalJourneys)).length)
        missing.push('Additional critical journeys need origin | destination pairs');
    for (const key of ['nearestHospital', 'nearestTrauma', 'traumaCentre']) {
        if (section.fields.some(field => field.key === key) && values[key] &&
            !/^(none|unknown|unavailable|no verified|pending|not found)\b/i.test(String(values[key]).trim()) &&
            !isGeneralOrTraumaHospital(String(values[key])))
            missing.push(`${key} must name a general hospital or trauma center`);
    }
    return missing;
}
